import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Modal } from "../../../components/Modal"
import { DiffType, SamplesContext, SamplesResponseType, UpdateSamplesContext, sampleMeasurements } from "../../../context/samplesContext"
import { formatNumber, formatOrdinal, MEASURE_COLOR_LIST } from "../../utils/helper"
import { useTableContext } from "../../../context/tableContext"
import clsx from "clsx"
import { getResolutionFromSample } from "../../utils/resolution"
import { markLegacyNormalized } from "../utils/manualLegacy"
import debounce from "lodash/debounce"
import { useCanEdit } from "../../../hooks/useCanEdit"

type DataType = {
  measure_index: number //テーブル表示用のindex
  original_index: number //サーバー本来のmeasure_index
  color: string
  measure_name: string
  result_real: number
  spec: number | string
  diff: number | string
  validate: string
  is_deleted: boolean
  measure_type: 'auto' | 'manual' | ''
}

const isZeroDiffValue = (diffValue: DataType['diff']): boolean => {
  if (typeof diffValue === 'number') {
    return diffValue === 0
  }

  if (typeof diffValue !== 'string') {
    return false
  }

  const trimmedDiffValue = diffValue.trim()
  if (trimmedDiffValue === '' || trimmedDiffValue === '-') {
    return false
  }

  const parsedDiffValue = Number(trimmedDiffValue.replace(/,/g, ''))
  return Number.isFinite(parsedDiffValue) && parsedDiffValue === 0
}

export function TableContainer({ setManualDrawingIndex, manualDrawingIndex }: { setManualDrawingIndex: React.Dispatch<React.SetStateAction<number | null>>, manualDrawingIndex: number | null }) {
  const { rowSample, diff } = useContext(SamplesContext)
  const { updateRowSample, fetchDiff } = useContext(UpdateSamplesContext)
  const { setColorMap, setIndexMap } = useTableContext()
  const { canEdit } = useCanEdit()

  // 手動/空行用の最小 original_index
  const MANUAL_START_INDEX = 31

  // inline編集用State
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')

  // 重複確認モーダルのステート
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pending, setPending] = useState<{ index: number, newName: string, duplicateIdx: number, duplicateType: "auto" | "manual" | "" } | null>(null)

  const [displayDiff, setDisplayDiff] = useState<DiffType | null>(null)
  const [tableData, setTableData] = useState<DataType[]>([])

  // AutoEditConfirmModal
  const [isAutoConfirmOpen, setIsAutoConfirmOpen] = useState(false)
  const [autoPendingCell, setAutoPendingCell] = useState<DataType | null>(null)

  const [resolution, setResolution] = useState<number>(0)

  useEffect(() => {
    if (!rowSample?.id || rowSample?.id === '') return // 未ロード時
    try {
      const r = getResolutionFromSample(rowSample)
      setResolution(r)
    } catch (_e) {
      console.error('Resolution determination failed:', _e)
      alert('解像度を特定できませんでした。デバイス設定やサンプルデータを確認してください。')
      setResolution(0)
    }
  }, [rowSample?.id, rowSample?.sampleMeasurements])

  // diff 更新を反映
  useEffect(() => {
    setDisplayDiff(diff)
  }, [diff])

  const debouncedFetchDiff = useMemo(
    () =>
      debounce((sample: SamplesResponseType) => {
        fetchDiff(sample)
      }, 500),
    [fetchDiff]
  )

  useEffect(() => {
    return () => {
      debouncedFetchDiff.cancel()
    }
  }, [debouncedFetchDiff])

  // fetchDiff のデバウンス
  useEffect(() => {
    const { part_number, sample_number, repeat, kind, time, size } = rowSample
    const isIncomplete =
      (!part_number && !sample_number) ||
      repeat === null ||
      repeat === undefined ||
      (kind === 'sample' && (time === null || time === undefined)) ||
      size === null ||
      size === undefined

    if (isIncomplete) {
      setDisplayDiff(null)
      debouncedFetchDiff.cancel()
      return
    }

    debouncedFetchDiff(rowSample)
  }, [debouncedFetchDiff, rowSample])

  // テーブル表示用データを構築
  const mapTableData = useCallback((): DataType[] => {
    const active = rowSample.sampleMeasurements
      .filter(sm => !(sm.measure_type === 'auto' && sm.is_deleted))
      .sort((a, b) => a.measure_index - b.measure_index)
      .map((sm, idx) => ({
        ...sm,
        original_index: sm.measure_index,
        measure_index: idx + 1,
      }))

    const used = new Set<number>(active.map(sm => sm.original_index))

    const maxUsed = used.size ? Math.max(...used) : 0
    let nextOriginal = Math.max(maxUsed + 1, MANUAL_START_INDEX)

    return MEASURE_COLOR_LIST.map((base, index) => {
      const sm = active[index]
      if (sm) {
        const pt = displayDiff?.data?.points.find(p => p.measure_name === sm.measure_name)
        return {
          measure_index: sm.measure_index,
          original_index: sm.original_index,
          color: base.color,
          measure_name: sm.measure_name,
          result_real: sm.result_real,
          spec: pt?.spec ?? '-',
          diff: pt?.diff ?? '-',
          validate: pt
            ? pt.spec == null
              ? "-"
              : pt.validate == null
                ? "-"
                : pt.validate
                  ? "OK"
                  : "NG"
            : "",
          is_deleted: sm.is_deleted,
          measure_type: sm.measure_type,
        }
      } else {

        while (used.has(nextOriginal)) {
          nextOriginal++
        }
        const assigned = nextOriginal
        used.add(assigned)
        nextOriginal++

        return {
          measure_index: base.measure_index,
          original_index: assigned,
          color: base.color,
          measure_name: "",
          result_real: 0,
          spec: "",
          diff: "",
          validate: "",
          is_deleted: true,
          measure_type: ""
        }
      }
    })
  }, [rowSample, displayDiff])

  useEffect(() => {
    const data = mapTableData()
    setTableData(data)

    const cm = new Map<number, string>()
    const im = new Map<number, number>()
    data.forEach((row, i) => {
      cm.set(row.original_index, MEASURE_COLOR_LIST[i].color)
      im.set(row.original_index, row.measure_index)
    })
    setColorMap(cm)
    setIndexMap(im)
  }, [rowSample, displayDiff, mapTableData, setColorMap, setIndexMap])

  const onNameClick = (cell: DataType) => {
    if (!canEdit) return
    if (cell.measure_type === 'auto') return
    setEditingIndex(cell.original_index)
    setEditingName(cell.measure_name)
  }

  const applyNameChange = (idx: number, name: string) => {
    if (!name.trim()) {
      setEditingIndex(null)
      return
    }
    const dupIdx = rowSample.sampleMeasurements.findIndex(
      (m) =>
        m.measure_name === name &&
        !m.is_deleted &&
        m.measure_index !== idx
    )
    if (dupIdx !== -1) {
      const dup = rowSample.sampleMeasurements[dupIdx]
      setPending({
        index: idx,
        newName: name,
        duplicateIdx: dupIdx,
        duplicateType: dup.measure_type,
      })
      setConfirmOpen(true)
    } else {
      commitChange(idx, name, null)
    }
  }

  const commitChange = (idx: number, name: string, pendingInfo: { duplicateIdx: number, duplicateType: "auto" | "manual" | "" } | null) => {
    // 1. 元データをコピー
    let updated = [...rowSample.sampleMeasurements]
    const autoData = updated.find((m) => m.measure_type === 'auto')

    if (pendingInfo) {
      const { duplicateIdx, duplicateType } = pendingInfo
      const originalIndex = updated[duplicateIdx].measure_index

      // 2. auto->manual　上書きのときはautoを論理削除
      if (duplicateType === 'auto') {
        updated = updated.map((m, i) =>
          i === duplicateIdx && m.measure_type === 'auto'
            ? { ...m, is_deleted: true }
            : m
        )
        const manualAtIdx = updated.find(
          m => m.measure_type === 'manual' && m.measure_index === idx
        )
        if (manualAtIdx) {
          updated = updated.filter((m) => m !== manualAtIdx)
          {
            const manualEntry: sampleMeasurements = {
              ...manualAtIdx,
              measure_index: originalIndex,
              measure_name: name,
            }
            markLegacyNormalized(manualEntry)
            updated.push(manualEntry)
          }
        } else {
          const manualEntry: sampleMeasurements = {
            measure_index: originalIndex,
            measure_name: name,
            image_index: 0,
            resolution: resolution || autoData?.resolution as number, // FIXME: 暫定対応
            // resolution: autoData?.resolution as number, // FIXME: 暫定対応
            result_pixel: 0,
            result_real: 0,
            result_point: [],
            measure_type: 'manual',
            time: rowSample.sampleMeasurements[0]?.time,
            is_deleted: false,
          }
          markLegacyNormalized(manualEntry)
          updated.push(manualEntry)
        }
      }

      if (duplicateType === 'manual') {
        updated = updated.filter(
          m => !(m.measure_type === 'manual' && m.measure_index === originalIndex)
        )
        const manualAtIdx = updated.find(
          m => m.measure_type === 'manual' && m.measure_index === idx
        )
        const autoData = updated.find((m) => m.measure_type === 'auto')

        if (manualAtIdx) {
          updated = updated.filter(m => m !== manualAtIdx)
          const manualEntry: sampleMeasurements = {
            ...manualAtIdx,
            measure_index: originalIndex,
            measure_name: name,
          }
          markLegacyNormalized(manualEntry)
          updated.push(manualEntry)
        } else {
          const manualEntry: sampleMeasurements = {
            measure_index: originalIndex,
            measure_name: name,
            image_index: 0,
            resolution: resolution || autoData?.resolution as number, // FIXME: 暫定対応
            // resolution: autoData?.resolution as number, // FIXME: 暫定対応
            result_pixel: 0,
            result_real: 0,
            result_point: [],
            measure_type: 'manual',
            time: rowSample.sampleMeasurements[0]?.time,
            is_deleted: false,
          }
          markLegacyNormalized(manualEntry)
          updated.push(manualEntry)
        }
      }
    } else {
      // ── 重複なし：そのセルの manual があれば名前更新、なければ追加 ──
      const exIdx = updated.findIndex(m => m.measure_index === idx && m.measure_type === "manual")

      if (exIdx >= 0) {
        updated[exIdx] = { ...updated[exIdx], measure_name: name }
      } else {
        const existing = updated.map(m => m.measure_index)
        const maxIdx = existing.length ? Math.max(...existing) : 0
        const newOriginal = Math.max(maxIdx + 1, MANUAL_START_INDEX)

        const manualEntry: sampleMeasurements = {
          measure_index: newOriginal,
          measure_name: name,
          image_index: autoData?.image_index as number,
          resolution: resolution || autoData?.resolution as number, // FIXME: 暫定対応
          // resolution: autoData?.resolution as number, // FIXME: 暫定対応
          result_pixel: 0,
          result_real: 0,
          result_point: [],
          measure_type: 'manual',
          time: rowSample.sampleMeasurements[0].time,
          is_deleted: false,
        }
        markLegacyNormalized(manualEntry)
        updated.push(manualEntry)
      }
    }

    updateRowSample({ sampleMeasurements: updated })
    setEditingIndex(null)
    setConfirmOpen(false)
  }

  const onConfirmOK = () => {
    if (pending) {
      commitChange(pending.index, pending.newName, {
        duplicateIdx: pending.duplicateIdx,
        duplicateType: pending.duplicateType
      })
      setPending(null)
    }
  }

  const onConfirmCancel = () => {
    setPending(null)
    setConfirmOpen(false)
    setEditingIndex(null)
  }

  // 削除
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteMeasure, setDeleteMeasure] = useState<DataType>()
  const openModal = useCallback(() => setIsModalOpen(true), [])
  const closeModal = useCallback(() => setIsModalOpen(false), [])
  const handleDeleteClick = useCallback((cell: DataType) => {
    if (!canEdit) return
    setDeleteMeasure(cell)
    openModal()
  }, [canEdit, openModal])

  // 編集
  const handleResultCellClick = useCallback((cell: DataType) => {
    if (!canEdit) return

    if (cell.measure_type === 'auto') {
      setAutoPendingCell(cell)
      setIsAutoConfirmOpen(true)
    } else {
      setManualDrawingIndex(cell.original_index)
    }
  }, [canEdit, setManualDrawingIndex])

  const onAutoConfirmOK = useCallback(() => {
    if (!autoPendingCell) return
    const idx = autoPendingCell.original_index

    const autoData = rowSample.sampleMeasurements.find(
      m => m.measure_index === idx && m.measure_type === 'auto'
    )
    let updated = rowSample.sampleMeasurements.map(m =>
      m.measure_index === idx && m.measure_type === 'auto'
        ? { ...m, is_deleted: true }
        : m
    )

    const manualEntry: sampleMeasurements = {
      measure_index: idx,
      measure_name: autoData?.measure_name!,
      image_index: autoData?.image_index!,
      resolution: resolution || autoData?.resolution!, // FIXME: 暫定対応
      // resolution: autoData?.resolution!, // FIXME: 暫定対応
      result_pixel: autoData?.result_pixel!,
      result_real: autoData?.result_real!,
      result_point: autoData?.result_point!,
      measure_type: 'manual',
      time: autoData?.time!,
      is_deleted: false,
    }
    markLegacyNormalized(manualEntry)
    updated.push(manualEntry)

    updateRowSample({ sampleMeasurements: updated })
    setManualDrawingIndex(idx)

    setIsAutoConfirmOpen(false)
    setAutoPendingCell(null)
  }, [autoPendingCell, rowSample, updateRowSample, setManualDrawingIndex, resolution])

  const onAutoConfirmCancel = useCallback(() => {
    setIsAutoConfirmOpen(false)
    setAutoPendingCell(null)
  }, [])

  return (
    <div className="w-[453px] h-[387px] overflow-y-scroll text-white overflow-x-hidden desktop-xmd:w-[473px] desktop-xmd:h-[615px]">
      <table className="min-w-max border-separate border-spacing-0 table-fixed w-full">
        <thead className="sticky top-0">
          <tr className="text-[12px] [&>th]:font-medium">
            <th
              rowSpan={2}
              className="bg-[#3E3E3E] w-[31px] h-[53px] sticky
              before:content-[''] before:absolute before:inset-0
              before:border before:border-[#adadad]"
            >
              No.
            </th>
            <th
              rowSpan={2}
              className="bg-[#3E3E3E] w-[40px] sticky
              before:content-[''] before:absolute before:inset-0
              before:border-t before:border-b before:border-r before:border-[#adadad]"
            >
              線色
            </th>
            <th
              rowSpan={2}
              className="bg-[#3E3E3E] w-[117px] sticky
              before:content-[''] before:absolute before:inset-0
              before:border-t before:border-b before:border-r before:border-[#adadad]"
            >
              採寸箇所
            </th>
            <th
              rowSpan={2}
              className="bg-[#3E3E3E] w-[70px] sticky
              before:content-[''] before:absolute before:inset-0
              before:border-t before:border-b before:border-r before:border-[#adadad]"
            >
              項目削除
            </th>
            {Array.isArray(displayDiff?.data?.points) && displayDiff?.data?.points.length !== 0 &&
              <th
                colSpan={1}
                className="bg-[#A5A5A5] w-[75px] h-[30px] sticky
                before:content-[''] before:absolute before:inset-0
                before:border-t before:border-b before:border-r before:border-[#d6d6d6]"
              >
                <div className={`flex items-center justify-center w-full h-full`}>
                  {rowSample.kind === 'final' ? 'FINAL' : formatOrdinal(rowSample.time)}
                </div>
              </th>
            }
            <th
              colSpan={1}
              className="bg-[#A5A5A5] w-[75px] h-[30px] sticky
              before:content-[''] before:absolute before:inset-0
              before:border-t before:border-b before:border-r before:border-[#d6d6d6]"
            >
              <div className={`flex items-center justify-center w-full h-full ${rowSample.time && 'border-b-[4px] border-[#688CF5]'}`}>
                {rowSample.kind === 'final' ? 'FINAL' : formatOrdinal(rowSample.time)}
              </div>
            </th>
            <th
              rowSpan={2}
              className="bg-[#3E3E3E] w-[52px] sticky
              before:content-[''] before:absolute before:inset-0
              before:border-t before:border-b before:border-r before:border-[#adadad]"
            >
              差寸
            </th>
          </tr>
          <tr className="text-[14px]">
            {displayDiff?.data?.condition.hasSpec &&
              <th
                className="bg-black w-[70px] font-medium sticky
                before:content-[''] before:absolute before:inset-0
                before:border-t before:border-b before:border-r before:border-[#adadad]"
              >
                指示寸法
              </th>
            }
            <th
              className="bg-black w-[70px] font-medium sticky
              before:content-[''] before:absolute before:inset-0
              before:border-t before:border-b before:border-r before:border-[#adadad]"
            >
              検寸結果
            </th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((el, index) => (
            <tr key={el.original_index} className={clsx(
              manualDrawingIndex === el.original_index && 'bg-[#616161]',
              !(manualDrawingIndex === el.original_index) && (index % 2 === 1 ? 'bg-[#272727]' : 'bg-[#000000]')
            )} >
              <td
                className="text-center h-[36px]"
                style={{ borderLeft: '1px solid #adadad', borderBottom: '1px solid #3E3E3E', borderRight: '1px solid #3E3E3E' }}
              >
                {formatNumber(index + 1)}
              </td>
              <td style={{ backgroundColor: el.color }} className="w-[44px]" />
              <td
                style={{ borderRight: '1px solid #adadad', borderBottom: '1px solid #3E3E3E' }}
                className={`text-center ${el.measure_type === 'auto' ? 'cursor-default' : 'cursor-text'}`}
                onClick={() => onNameClick(el)}
              >
                {editingIndex === el.original_index ? (
                  <input
                    autoFocus
                    className="w-full bg-gray-50 text-black text-center"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={() => { applyNameChange(el.original_index, editingName) }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyNameChange(el.original_index, editingName)
                      if (e.key === "Escape") setEditingIndex(null)
                    }}
                  />
                ) : (el.measure_name)}
              </td>
              <td style={{ borderRight: '1px solid #adadad', borderBottom: '1px solid #3E3E3E' }}>
                {(el.measure_name || el.result_real) ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <button
                      className={`w-[50px] h-[25px] rounded-[5px] bg-white text-black text-[12px] ${!canEdit ? 'cursor-not-allowed' : ''}`}
                      onClick={() => handleDeleteClick(el)}
                      aria-disabled={!canEdit}
                    >
                      削除
                    </button>
                  </div>
                ) : ''}
              </td>
              {displayDiff?.data?.condition.hasSpec &&
                <td style={{ borderRight: '1px solid #adadad', borderBottom: '1px solid #3E3E3E' }} className="text-center">
                  {el.spec !== 0 ? el.spec : ''}
                </td>
              }
              <td
                style={{ borderRight: '1px solid #adadad', borderBottom: '1px solid #3E3E3E' }}
                className="text-center"
                onClick={() => handleResultCellClick(el)}
              >
                {el.result_real !== 0 ? el.result_real : manualDrawingIndex === el.original_index ? '描画中' : ''}
              </td>
              <td style={{ borderRight: '1px solid #adadad', borderBottom: '1px solid #3E3E3E' }}
                className={`text-center ${el.diff?.toString().startsWith('-') ? 'first-letter:tracking-[3px]' : ''}`}
              >
                <span className={`${el.validate === 'OK' || el.diff === '-' || isZeroDiffValue(el.diff) ? 'text-white' : 'text-[#FF0000]'}`}>{el.diff}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <DeleteModal isModalOpen={isModalOpen} closeModal={closeModal} deleteMeasureInfo={deleteMeasure} />
      <DuplicateModal isModalOpen={confirmOpen} closeModal={onConfirmCancel} pending={pending} onConfirmOK={onConfirmOK} />
      <AutoEditConfirmModal isModalOpen={isAutoConfirmOpen} closeModal={onAutoConfirmCancel} onConfirmOK={onAutoConfirmOK} />
    </div>
  )
}

function DeleteModal({ isModalOpen, closeModal, deleteMeasureInfo }: { isModalOpen: boolean, closeModal: () => void, deleteMeasureInfo?: DataType }) {
  const { rowSample } = useContext(SamplesContext)
  const { updateRowSample } = useContext(UpdateSamplesContext)

  // delete:最初の確認 confirm:復活確認
  const [stage, setStage] = useState<'delete' | 'confirm'>('delete')

  const handleCloseAll = useCallback(() => {
    setStage('delete')
    closeModal()
  }, [closeModal])

  // 1回目の「削除しますか？」でOKしたとき
  const handleFirstOk = useCallback(() => {
    if (!deleteMeasureInfo) return
    const { measure_name, measure_type, measure_index, original_index } = deleteMeasureInfo

    // 手動(manual)かつ、同名の自動(auto)が論理削除済みか探す
    const hasDeletedAuto = rowSample.sampleMeasurements?.some(
      sm =>
        sm.measure_name === measure_name &&
        sm.measure_type === 'auto' &&
        sm.is_deleted
    )

    // manual:物理削除 auto:論理削除
    let updated = [...rowSample.sampleMeasurements]
    if (measure_type === 'manual') {
      updated = updated
        .filter(sm => !(sm.measure_name === measure_name && sm.measure_type === 'manual' && sm.measure_index === original_index))
        .map((m) => measure_index < m.measure_index ? { ...m, measure_index: m.measure_index - 1 } : m)
    } else {
      updated = updated.map(sm => sm.measure_name === measure_name ? { ...sm, is_deleted: true } : sm)
      updated = updated.map(m => measure_index < m.measure_index ? { ...m, measure_index: m.measure_index - 1 } : m)
    }

    updateRowSample({ sampleMeasurements: updated })

    // 同名auto復活確認が必要ならステージを進める
    if (measure_type === 'manual' && hasDeletedAuto) {
      setStage('confirm')
      return
    }

    handleCloseAll()
  }, [deleteMeasureInfo, rowSample, updateRowSample, handleCloseAll])

  const handleSecondOk = useCallback(() => {
    if (!deleteMeasureInfo) return
    const { measure_name } = deleteMeasureInfo

    const restoreDataIdx = rowSample.sampleMeasurements.find(m => m.measure_name === measure_name && m.measure_type === 'auto' && m.is_deleted)?.measure_index

    // 同名の自動(auto)を復活させる
    const restored = rowSample.sampleMeasurements
      .map(sm => {
        const nameMatch = sm.measure_name.trim() === measure_name.trim()
        const isTargetAutoDeleted = nameMatch && sm.measure_type === 'auto' && sm.is_deleted

        if (isTargetAutoDeleted) {
          return {
            ...sm,
            measure_index: sm.measure_index,
            is_deleted: false,
          }
        }
        if (sm.measure_index >= (restoreDataIdx || 0)) {
          return {
            ...sm,
            measure_index: sm.measure_index + 1
          }
        }
        return sm
      })

    updateRowSample({ sampleMeasurements: restored })
    handleCloseAll()
  }, [deleteMeasureInfo, rowSample, updateRowSample, handleCloseAll])

  const handleCancel = useCallback(() => {
    handleCloseAll()
  }, [handleCloseAll])

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={closeModal} className="flex flex-col items-center justify-center w-[414px] h-[189px]">
        <span className="text-black">採寸項目を削除しますか？</span>
        <div className="space-x-[8px] mt-[15px]">
          <button
            onClick={handleCancel}
            className="w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
          >
            キャンセル
          </button>
          <button
            className="w-[160px] h-[47px] bg-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
            onClick={handleFirstOk}
          >
            削除
          </button>
        </div>
      </Modal>
      {isModalOpen && stage === 'confirm' && (
        <Modal isOpen={true} onClose={handleCancel} className="flex flex-col items-center justify-center w-[414px] h-[189px]">
          <span className="text-black">削除した自動採寸箇所を復活させますか？</span>
          <div className="space-x-[8px] mt-[15px]">
            <button
              onClick={handleCancel}
              className="w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
            >
              キャンセル
            </button>
            <button
              onClick={handleSecondOk}
              className="w-[160px] h-[47px] bg-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
            >
              OK
            </button>
          </div>
        </Modal>
      )}
    </>
  )
}

function DuplicateModal({
  isModalOpen,
  closeModal,
  pending,
  onConfirmOK
}: {
  isModalOpen: boolean,
  closeModal: () => void,
  pending: { index: number, newName: string, duplicateIdx: number, duplicateType: "auto" | "manual" | "" } | null,
  onConfirmOK: () => void,
}) {
  return (
    <Modal isOpen={isModalOpen} onClose={closeModal} className="flex flex-col items-center justify-center min-w-[414px] h-[189px] px-[25px]">
      <span className="text-black text-center">
        <p>"{pending?.newName}"はすでに存在しています。</p>
        <p>上書きしますか？</p>
      </span>
      <div className="space-x-[8px] mt-[15px]">
        <button
          onClick={closeModal}
          className="w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
        >
          キャンセル
        </button>
        <button
          onClick={onConfirmOK}
          className="w-[160px] h-[47px] bg-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
        >
          OK
        </button>
      </div>
    </Modal>
  )
}

function AutoEditConfirmModal({
  isModalOpen,
  closeModal,
  onConfirmOK
}: {
  isModalOpen: boolean,
  closeModal: () => void,
  onConfirmOK: () => void
}) {
  return (
    <Modal isOpen={isModalOpen} onClose={closeModal} className="flex flex-col items-center justify-center min-w-[414px] h-[189px] px-[25px]">
      <span className="text-black text-center">
        <p>こちらは自動採寸データです。</p>
        <p>手動採寸で上書きしますか？</p>
      </span>
      <div className="space-x-[8px] mt-[15px]">
        <button
          onClick={closeModal}
          className="w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
        >
          キャンセル
        </button>
        <button
          onClick={onConfirmOK}
          className="w-[160px] h-[47px] bg-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
        >
          OK
        </button>
      </div>
    </Modal>
  )
}
